/**
 * Qualitas AML compliance — one screen, nothing asked twice.
 *
 * Everything identity verification already proved (name, birth date, phone,
 * gender, nationality) arrives prefilled and renders as a confirmable summary
 * carrying its provenance; it becomes an input only when the user taps Edit,
 * or when we genuinely do not hold it. The one thing that cannot be derived —
 * the AML declaration — is captured entirely through single-tap choices, so
 * there is no free-text field left on this screen. Progressive disclosure
 * keeps it one screen instead of a wizard.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated from 'react-native-reanimated'
import { ShieldCheck, Clock, CheckCircle2, AlertCircle, Globe } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ConfirmedField } from '@/components/ui/ConfirmedField'
import { ScreenState } from '@/components/ui/ScreenState'
import { ScreenHeader, safeInternalReturnPath } from '@/components/ui/ScreenHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { TextInput } from '@/components/ui/TextInput'
import { DatePicker } from '@/components/ui/DatePicker'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Pressable } from '@/components/ui/Pressable'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ChoicePillGroup } from '@/components/compliance/ChoicePillGroup'
import { ManualReviewNotice } from '@/components/compliance/ManualReviewNotice'
import { QualitasDisclaimerScroll } from '@/components/compliance/QualitasDisclaimerScroll'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance, type Entrance } from '@/lib/hooks/use-entrance'
import { useSemanticColors, SPACING, RADIUS, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useCountries } from '@/lib/hooks/use-countries'
import { useAmlFormBootstrap, useSubmitAmlDeclaration } from '@/lib/hooks/use-aml-compliance'
import { amlFormSchema, emptyAmlFormValues } from '@/lib/validations/aml.schema'
import { formatSvDateDisplay } from '@/lib/utils/dates'
import {
  AML_ECONOMIC_ACTIVITIES,
  AML_FUND_ORIGINS,
  AML_GENDERS,
  AML_INCOME_RANGES,
  isAmlFormComplete,
  requiresManualPepReview,
  type AmlEconomicActivity,
  type AmlFormValues,
  type AmlFundOrigin,
  type AmlGender,
  type AmlIncomeRange,
  type AmlPrefillField,
  type AmlPrefillSource,
} from '@/lib/types/aml.types'
import { AML_SV_ISO2, QUALITAS_AML_PARTNER } from '@/lib/constants/aml'
import { isCoveragePartnerBrandingVisible } from '@/lib/config/insurance-partners'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { notification, NotificationType } from '@/lib/utils/haptics'

/** Height of one TextInput/Select row in the form the skeleton stands in for. */
const AML_FIELD_HEIGHT = 52

type FieldErrors = Partial<Record<keyof AmlFormValues, string>>
type Colors = ReturnType<typeof useSemanticColors>

function SectionLabel({
  title,
  subtitle,
  action,
  colors,
}: {
  title: string
  subtitle?: string
  /** Optional trailing control, e.g. the one-tap edit affordance. */
  action?: React.ReactNode
  colors: Colors
}) {
  return (
    // No horizontal padding: the label, its action and its helper are the only
    // blocks on this screen that were inset a further SPACING.xs, so the
    // heading of a card started 4pt right of the card it labels and the "Edit"
    // link stopped 4pt short of the card's right edge. Nothing on the screen
    // shared a left rule. The cards own the gutter; the label sits on it.
    <View style={{ gap: 4, marginBottom: SPACING.sm }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <Text
          style={{
            flex: 1,
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {title}
        </Text>
        {action}
      </View>
      {subtitle ? (
        <Text
          style={{
            fontSize: 13,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            lineHeight: 18,
          }}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  )
}

/**
 * Cancels the gap a form control reserves under itself.
 *
 * TextInput, DatePicker and Select each carry `marginBottom: FORM_FIELD_GAP`
 * (32) so screens can stack them without thinking about rhythm. Inside a
 * `Group` the card's own 16pt `gap` already owns that rhythm, and the two
 * stacked: an editable row left a ~48pt hole under itself while a read-only
 * `ConfirmedField` — which reserves nothing — sat at 16pt. Same card, two
 * rhythms, and the date field looked like it had an invisible error slot.
 * Wrapping the control cancels its reserved slot so every row lands on the
 * card's beat; the control keeps its own gap for every other caller.
 */
function FieldRow({ children }: { children: React.ReactNode }) {
  return <View style={{ marginBottom: -FORM_FIELD_GAP }}>{children}</View>
}

/**
 * A datum Tago already holds, shown for confirmation instead of re-asked.
 *
 * Its label wears the card's one label rule — 14pt FONTS.body, textPrimary at
 * 0.75, +0.2 tracking — because that is what TextInput, DatePicker, Select and
 * ChoicePillGroup all draw above their own controls. It used to be a 13pt
 * textMuted caption, so the identity card alternated grey-caption,
 * dark-label, grey-caption, dark-label the whole way down and read as two form
 * components bolted together.
 *
 * What tells a fact apart from a field is not the label — it is what sits
 * under it and how close. A control is a separate object and gets SPACING.md
 * of air; a value is the label's own answer and stays bound to it at
 * SPACING.xs. Same typography, different distance, no ambiguity.
 *
 * It lives on this screen rather than in components/compliance/, because the
 * rule it has to match is this card's and this is the only card that has one.
 */
function Group({ children, colors }: { children: React.ReactNode; colors: Colors }) {
  return (
    <View
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        padding: SPACING.md,
        // Wider than the SPACING.md a label leaves above its own control, and
        // that difference is the only thing telling the reader which label
        // owns which control. At 16/16 the declarations card was a flat
        // stack — question, pills, question, pills, every gap identical — and
        // the boxed fields only got away with it because a 52pt filled input
        // is impossible to mistake for a heading. A pill row is not.
        gap: SPACING.lg,
        overflow: 'hidden',
      }}
    >
      {children}
    </View>
  )
}

function AnimatedBlock({ entering, children }: { entering: Entrance; children: React.ReactNode }) {
  // `entering` is undefined under reduced motion and on every remount after
  // the first, which is exactly when Animated.View must not be used at all.
  if (!entering) return <View style={{ gap: SPACING.sm }}>{children}</View>
  return (
    <Animated.View entering={entering} style={{ gap: SPACING.sm }}>
      {children}
    </Animated.View>
  )
}

export default function AmlComplianceScreen() {
  const { t } = useTranslation()

  /**
   * Who to name in the STATUS copy — a promise about what will happen, not a
   * consent to anything.
   *
   * isCoveragePartnerBrandingVisible() is false until COVERAGE_STATUS is
   * 'live', because an insurer that is not issuing policies is not on the hook
   * for anything. Naming it in "cuando la cobertura se active, X valida contra
   * listas como OFAC" told every user which company would screen them, before
   * that company had signed anything.
   *
   * Deliberately NOT applied to the consent wording on this screen —
   * cross_border_body, cross_border_label, section_disclaimer and the intro.
   * Those are what the person is agreeing to, and the record we store names
   * "Quálitas Compañía de Seguros, S.A., including its systems located in
   * Mexico". Showing "la aseguradora" while filing a consent naming Quálitas
   * would make the consent unprovable, which is worse than naming an
   * unsigned partner: Art. 54 asks us to show exactly what they read.
   */
  const amlPartnerLabel = isCoveragePartnerBrandingVisible()
    ? QUALITAS_AML_PARTNER.displayName
    : t('aml.partner_generic')
  const router = useRouter()
  const { from } = useLocalSearchParams<{ from?: string }>()
  /**
   * El origen viaja en la URL cuando el huésped llegó acá desde la ficha de un
   * vehículo ("Verificarme para reservar"). Terminar el cuestionario tiene que
   * devolverlo a ESE carro —listo para reservar— y no dejarle un hub de por
   * medio para que lo busque otra vez. Sin `from`, el hub de siempre.
   */
  const returnTo = safeInternalReturnPath(from, '/(tabs)/profile/verification')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()

  const bootstrap = useAmlFormBootstrap()
  const enter = useEntrance(!bootstrap.isLoading)
  const submitMutation = useSubmitAmlDeclaration()
  const { data: countries = [] } = useCountries()

  const [values, setValues] = useState<AmlFormValues>(emptyAmlFormValues())
  const [errors, setErrors] = useState<FieldErrors>({})
  const [hydrated, setHydrated] = useState(false)
  // Verified identity stays read-only until the user asks to change it. A blank
  // input for a datum already checked against the official registry is exactly
  // the re-capture this screen exists to delete.
  const [editingIdentity, setEditingIdentity] = useState(false)

  useEffect(() => {
    if (!bootstrap.data || hydrated) return
    setValues(bootstrap.data.values)
    setHydrated(true)
  }, [bootstrap.data, hydrated])

  const declaration = bootstrap.data?.declaration ?? null
  const isReadOnlyComplete =
    !!declaration && isAmlFormComplete(declaration.status) && declaration.status !== 'needs_update'
  const showRejected = declaration?.status === 'rejected' || declaration?.status === 'needs_update'

  const prefillSources = useMemo<Partial<Record<AmlPrefillField, AmlPrefillSource>>>(
    () => bootstrap.data?.prefill.sources ?? {},
    [bootstrap.data],
  )

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.id, label: c.name })),
    [countries],
  )

  const countryNameById = useMemo(() => new Map(countries.map((c) => [c.id, c.name])), [countries])

  const svCountryId = useMemo(
    () => countries.find((c) => c.iso2.toUpperCase() === AML_SV_ISO2)?.id ?? null,
    [countries],
  )

  const nationalityIsForeign = useMemo(() => {
    if (!values.nationalityCountryId || !svCountryId) return false
    return values.nationalityCountryId !== svCountryId
  }, [values.nationalityCountryId, svCountryId])

  const patch = useCallback(<K extends keyof AmlFormValues>(key: K, value: AmlFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const markDisclaimerPresented = useCallback(() => {
    patch('disclaimerScrollCompleted', true)
  }, [patch])

  const incomeOptions = useMemo(
    () => AML_INCOME_RANGES.map((value) => ({ value, label: t(`aml.income_option.${value}`) })),
    [t],
  )
  const fundOptions = useMemo(
    () => AML_FUND_ORIGINS.map((value) => ({ value, label: t(`aml.fund_option.${value}`) })),
    [t],
  )
  const genderOptions = useMemo(
    () => AML_GENDERS.map((value) => ({ value, label: t(`aml.gender_option.${value}`) })),
    [t],
  )
  const activityOptions = useMemo(
    () =>
      AML_ECONOMIC_ACTIVITIES.map((value) => ({ value, label: t(`aml.activity_option.${value}`) })),
    [t],
  )
  const yesNo = useMemo(
    () => [
      { value: true as const, label: t('common.yes') },
      { value: false as const, label: t('common.no') },
    ],
    [t],
  )

  const sourceLabel = useCallback(
    (field: AmlPrefillField): string => {
      const source = prefillSources[field]
      if (source === 'identity_document') return t('aml.source_identity_document')
      if (source === 'profile') return t('aml.source_profile')
      return t('aml.source_confirmed')
    },
    [prefillSources, t],
  )

  const needsManualPepReview = requiresManualPepReview(values)

  const translateError = (key?: string) => {
    if (!key) return undefined
    return key.startsWith('aml.') ? t(key) : key
  }

  const handleSubmit = async () => {
    const payload: AmlFormValues = {
      ...values,
      originCountryId: nationalityIsForeign ? values.originCountryId : null,
    }

    const parsed = amlFormSchema.safeParse(payload)
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]
        if (typeof key === 'string' && !fieldErrors[key as keyof AmlFormValues]) {
          fieldErrors[key as keyof AmlFormValues] = translateError(issue.message) ?? issue.message
        }
      }
      if (nationalityIsForeign && !payload.originCountryId) {
        fieldErrors.originCountryId = t('aml.error_origin_country')
      }
      // Withholding the international-transfer authorization is a decision the
      // law protects, not a field the user got wrong: it never renders as an
      // error, and when it is the only thing missing the screen says what that
      // means instead of failing the form at them.
      const onlyConsentMissing =
        !!fieldErrors.crossBorderTransferAccepted && Object.keys(fieldErrors).length === 1
      delete fieldErrors.crossBorderTransferAccepted
      if (onlyConsentMissing) {
        setErrors(fieldErrors)
        toast.info(t('aml.cross_border_declined_title'), {
          description: t('aml.cross_border_declined_body', {
            partner: QUALITAS_AML_PARTNER.displayName,
          }),
        })
        return
      }
      // An identity datum can only be corrected with its input on screen, so a
      // failure there opens the section instead of pointing at a summary row.
      if (
        fieldErrors.fullName ||
        fieldErrors.phone ||
        fieldErrors.dateOfBirth ||
        fieldErrors.gender ||
        fieldErrors.nationalityCountryId ||
        fieldErrors.residenceCountryId
      ) {
        setEditingIdentity(true)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      toast.error(t('aml.error_incomplete'))
      return
    }

    if (nationalityIsForeign && !payload.originCountryId) {
      setErrors({ originCountryId: t('aml.error_origin_country') })
      toast.error(t('aml.error_incomplete'))
      return
    }

    try {
      await submitMutation.mutateAsync(payload)
      void notification(NotificationType.Success)
      toast.success(t('aml.success_title'), { description: t('aml.success_body') })
      router.replace(returnTo as never)
    } catch (err) {
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('aml.error_submit')))
      captureError(err, { screen: 'aml-compliance', action: 'submit' })
    }
  }

  const renderBody = (): React.ReactNode => {
    if (isReadOnlyComplete && declaration) {
      const pending = declaration.status === 'under_review' || declaration.status === 'submitted'
      const approved = declaration.status === 'approved'
      return (
        <View style={{ flex: 1, paddingHorizontal: SPACING.lg }}>
          <ScreenHeader
            title={t('aml.title')}
            backTestID="aml.back"
            style={{ paddingHorizontal: 0, paddingTop: SPACING.sm, paddingBottom: SPACING.md }}
          />
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              paddingBottom: insets.bottom + SPACING['2xl'],
            }}
          >
            <View
              style={{
                backgroundColor: colors.surface,
                borderRadius: RADIUS.xl,
                borderWidth: 1,
                borderColor: colors.border,
                padding: SPACING.xl,
                gap: SPACING.md,
                alignItems: 'center',
              }}
              testID="aml.status-card"
            >
              {approved ? (
                <CheckCircle2 size={44} color={colors.success} strokeWidth={1.5} />
              ) : (
                <Clock size={44} color={COLORS.coral.DEFAULT} strokeWidth={1.5} />
              )}
              <Text
                style={{
                  fontSize: 22,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  textAlign: 'center',
                }}
              >
                {approved ? t('aml.status_approved_title') : t('aml.status_pending_title')}
              </Text>
              <Text
                style={{
                  fontSize: 15,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  lineHeight: 22,
                }}
              >
                {approved
                  ? t('aml.status_approved_body', { partner: amlPartnerLabel })
                  : t('aml.status_pending_body', { partner: amlPartnerLabel })}
              </Text>
              {pending ? (
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: colors.textMuted,
                    textAlign: 'center',
                    lineHeight: 18,
                  }}
                >
                  {t('aml.status_pending_note')}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={{ paddingBottom: insets.bottom + SPACING.md }}>
            <Button
              title={t('common.done')}
              onPress={() => router.replace(returnTo as never)}
              testID="aml.done-button"
            />
          </View>
        </View>
      )
    }

    // A field becomes an input only when we do not already hold it, or when the
    // user opened the section to correct something.
    const showInput = (field: AmlPrefillField): boolean => {
      if (editingIdentity) return true
      switch (field) {
        case 'fullName':
          return !values.fullName
        case 'phone':
          return !values.phone
        case 'dateOfBirth':
          return !values.dateOfBirth
        case 'gender':
          return !values.gender
        case 'nationalityCountryId':
          return !values.nationalityCountryId
        case 'residenceCountryId':
          return !values.residenceCountryId
      }
    }

    return (
      <View style={{ flex: 1 }}>
        <View style={{ paddingHorizontal: SPACING.lg }}>
          <ScreenHeader
            title={t('aml.title')}
            backTestID="aml.back"
            style={{ paddingHorizontal: 0, paddingTop: SPACING.sm, paddingBottom: SPACING.sm }}
          />
        </View>

        <KeyboardAwareView includeTopInset={false} bottomOffset={12} bounces>
          {/* No `paddingHorizontal` here: KeyboardAwareView already insets its
              content by SPACING.lg. Setting it again stacked to 48pt, so every
              card sat 24pt inside the sticky footer button below them and
              nothing on the screen shared a left or right edge. */}
          <View
            style={{
              paddingBottom: SPACING['3xl'] + 88,
              gap: SPACING.lg,
            }}
          >
            {/* Icon and title share a row; the paragraph runs the full card
                width beneath them. Nesting the paragraph in the icon's column
                spent a quarter of the card on a gutter only the first line
                needed and broke the copy into nine ragged lines. */}
            <AnimatedBlock entering={enter(0)}>
              <View
                style={{
                  gap: SPACING.sm,
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                  backgroundColor: isDark ? 'rgba(240,66,45,0.14)' : 'rgba(240,66,45,0.07)',
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: RADIUS.full,
                      backgroundColor: isDark ? 'rgba(240,66,45,0.22)' : 'rgba(240,66,45,0.12)',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ShieldCheck size={22} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
                  </View>
                  <Text
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 16,
                      fontFamily: FONTS.bodyBold,
                      color: colors.textPrimary,
                    }}
                  >
                    {t('aml.intro_title', { partner: QUALITAS_AML_PARTNER.displayName })}
                  </Text>
                </View>
                <Text
                  style={{
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    lineHeight: 20,
                  }}
                >
                  {t('aml.intro_body', { partner: QUALITAS_AML_PARTNER.displayName })}
                </Text>
              </View>
            </AnimatedBlock>

            {showRejected ? (
              <View
                style={{
                  flexDirection: 'row',
                  gap: SPACING.sm,
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                  backgroundColor: `${COLORS.coral.DEFAULT}12`,
                  borderWidth: 1,
                  borderColor: `${COLORS.coral.DEFAULT}33`,
                }}
              >
                <AlertCircle size={20} color={COLORS.coral.DEFAULT} />
                <Text
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                    lineHeight: 20,
                  }}
                >
                  {declaration?.rejectionReason || t('aml.needs_update_body')}
                </Text>
              </View>
            ) : null}

            <AnimatedBlock entering={enter(1)}>
              <SectionLabel
                title={t('aml.section_identity')}
                subtitle={t('aml.section_identity_helper')}
                colors={colors}
                action={
                  <Pressable
                    onPress={() => setEditingIdentity((prev) => !prev)}
                    accessibilityRole="button"
                    accessibilityLabel={
                      editingIdentity
                        ? t('aml.identity_edit_close_a11y')
                        : t('aml.identity_edit_a11y')
                    }
                    testID="aml.identity-edit"
                    // 44x44 comes from minHeight/minWidth, not from padding:
                    // the horizontal padding only pushed the label 4pt inside
                    // the row, so "Editar" ended short of the right edge every
                    // card on this screen lines up on.
                    style={{
                      minHeight: 44,
                      minWidth: 44,
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                    }}
                  >
                    {/* Matches the section label it sits beside: 13pt, same
                        face, same tracking. At 15pt the optional action
                        outweighed the heading it belongs to — the coral alone
                        already marks it as the action. */}
                    <Text
                      style={{
                        fontSize: 13,
                        fontFamily: FONTS.body,
                        color: colors.textAccent,
                        letterSpacing: 0.4,
                      }}
                    >
                      {editingIdentity ? t('common.done') : t('common.edit')}
                    </Text>
                  </Pressable>
                }
              />
              <Group colors={colors}>
                {showInput('fullName') ? (
                  <FieldRow>
                    <TextInput
                      label={t('aml.full_name')}
                      value={values.fullName}
                      onChangeText={(v) => patch('fullName', v)}
                      error={errors.fullName}
                      autoCapitalize="words"
                      textContentType="name"
                      autoComplete="name"
                      testID="aml.full-name"
                    />
                  </FieldRow>
                ) : (
                  <ConfirmedField
                    label={t('aml.full_name')}
                    value={values.fullName}
                    source={sourceLabel('fullName')}
                    testID="aml.summary-full-name"
                    colors={colors}
                  />
                )}

                {showInput('dateOfBirth') ? (
                  <FieldRow>
                    <DatePicker
                      label={t('aml.date_of_birth')}
                      value={values.dateOfBirth || null}
                      onChange={(iso) => patch('dateOfBirth', iso)}
                      error={errors.dateOfBirth}
                      maxDate={new Date().toISOString().slice(0, 10)}
                      testID="aml.date-of-birth"
                    />
                  </FieldRow>
                ) : (
                  <ConfirmedField
                    label={t('aml.date_of_birth')}
                    value={formatSvDateDisplay(values.dateOfBirth)}
                    source={sourceLabel('dateOfBirth')}
                    testID="aml.summary-date-of-birth"
                    colors={colors}
                  />
                )}

                {showInput('phone') ? (
                  <FieldRow>
                    <TextInput
                      label={t('aml.phone')}
                      value={values.phone}
                      onChangeText={(v) => patch('phone', v)}
                      error={errors.phone}
                      keyboardType="phone-pad"
                      textContentType="telephoneNumber"
                      autoComplete="tel"
                      testID="aml.phone"
                    />
                  </FieldRow>
                ) : (
                  <ConfirmedField
                    label={t('aml.phone')}
                    value={values.phone}
                    source={sourceLabel('phone')}
                    testID="aml.summary-phone"
                    colors={colors}
                  />
                )}

                {showInput('gender') ? (
                  <ChoicePillGroup
                    label={t('aml.gender')}
                    options={genderOptions}
                    value={values.gender}
                    onChange={(v) => patch('gender', v as AmlGender)}
                    error={errors.gender}
                    testIDPrefix="aml.gender"
                    columns={2}
                  />
                ) : (
                  <ConfirmedField
                    label={t('aml.gender')}
                    value={values.gender ? t(`aml.gender_option.${values.gender}`) : ''}
                    source={sourceLabel('gender')}
                    testID="aml.summary-gender"
                    colors={colors}
                  />
                )}

                {showInput('nationalityCountryId') ? (
                  <FieldRow>
                    <Select
                      label={t('aml.nationality')}
                      value={values.nationalityCountryId}
                      onChange={(id) => {
                        patch('nationalityCountryId', id)
                        if (svCountryId && id === svCountryId) patch('originCountryId', null)
                      }}
                      options={countryOptions}
                      placeholder={t('aml.nationality_placeholder')}
                      searchable
                      sheetTitle={t('aml.nationality')}
                      error={errors.nationalityCountryId}
                      testID="aml.nationality"
                    />
                  </FieldRow>
                ) : (
                  <ConfirmedField
                    label={t('aml.nationality')}
                    value={countryNameById.get(values.nationalityCountryId ?? '') ?? ''}
                    source={sourceLabel('nationalityCountryId')}
                    testID="aml.summary-nationality"
                    colors={colors}
                  />
                )}

                {showInput('residenceCountryId') ? (
                  <FieldRow>
                    <Select
                      label={t('aml.residence_country')}
                      value={values.residenceCountryId}
                      onChange={(id) => patch('residenceCountryId', id)}
                      options={countryOptions}
                      placeholder={t('aml.residence_country_placeholder')}
                      searchable
                      sheetTitle={t('aml.residence_country')}
                      error={errors.residenceCountryId}
                      testID="aml.residence-country"
                    />
                  </FieldRow>
                ) : (
                  <ConfirmedField
                    label={t('aml.residence_country')}
                    value={countryNameById.get(values.residenceCountryId ?? '') ?? ''}
                    source={sourceLabel('residenceCountryId')}
                    testID="aml.summary-residence-country"
                    colors={colors}
                  />
                )}

                {/* Only a non-Salvadoran nationality makes this question mean
                    anything, so it stays hidden for almost every renter. */}
                {nationalityIsForeign ? (
                  <FieldRow>
                    <Select
                      label={t('aml.origin_country')}
                      value={values.originCountryId}
                      onChange={(id) => patch('originCountryId', id)}
                      options={countryOptions}
                      placeholder={t('aml.origin_country_placeholder')}
                      searchable
                      sheetTitle={t('aml.origin_country')}
                      error={errors.originCountryId}
                      testID="aml.origin-country"
                    />
                  </FieldRow>
                ) : null}
              </Group>
            </AnimatedBlock>

            <AnimatedBlock entering={enter(2)}>
              <SectionLabel
                title={t('aml.section_economic')}
                subtitle={t('aml.section_economic_helper', {
                  partner: QUALITAS_AML_PARTNER.displayName,
                })}
                colors={colors}
              />
              <Group colors={colors}>
                <ChoicePillGroup
                  label={t('aml.income_range')}
                  options={incomeOptions}
                  value={values.incomeRange}
                  onChange={(v) => patch('incomeRange', v as AmlIncomeRange)}
                  error={errors.incomeRange}
                  testIDPrefix="aml.income"
                  columns={2}
                />
                {/* Closed catalogue, never a text box: the insurer governs
                    Actividad Económica with a code list, and prose cannot be
                    mapped onto it (QUALITAS_MAPEO §9 H4). */}
                <FieldRow>
                  <Select
                    label={t('aml.economic_activity')}
                    value={values.economicActivity}
                    onChange={(v) => patch('economicActivity', v as AmlEconomicActivity)}
                    options={activityOptions}
                    placeholder={t('aml.economic_activity_placeholder')}
                    sheetTitle={t('aml.economic_activity')}
                    error={errors.economicActivity}
                    testID="aml.economic-activity"
                  />
                </FieldRow>
                <ChoicePillGroup
                  label={t('aml.fund_origin')}
                  options={fundOptions}
                  value={values.fundOrigin}
                  onChange={(v) => patch('fundOrigin', v as AmlFundOrigin)}
                  error={errors.fundOrigin}
                  testIDPrefix="aml.fund-origin"
                />
              </Group>
            </AnimatedBlock>

            <AnimatedBlock entering={enter(3)}>
              <SectionLabel
                title={t('aml.section_declarations')}
                subtitle={t('aml.section_declarations_helper')}
                colors={colors}
              />
              <Group colors={colors}>
                <ChoicePillGroup
                  label={t('aml.is_pep')}
                  helper={t('aml.is_pep_helper')}
                  options={yesNo}
                  value={values.isPep}
                  onChange={(v) => patch('isPep', v)}
                  error={errors.isPep}
                  testIDPrefix="aml.is-pep"
                  columns={2}
                />
                <ChoicePillGroup
                  label={t('aml.is_party_member')}
                  options={yesNo}
                  value={values.isPoliticalPartyMember}
                  onChange={(v) => patch('isPoliticalPartyMember', v)}
                  error={errors.isPoliticalPartyMember}
                  testIDPrefix="aml.party-member"
                  columns={2}
                />
                <ChoicePillGroup
                  label={t('aml.has_pep_family')}
                  options={yesNo}
                  value={values.hasPepFamily}
                  onChange={(v) => patch('hasPepFamily', v)}
                  error={errors.hasPepFamily}
                  testIDPrefix="aml.pep-family"
                  columns={2}
                />
                <ChoicePillGroup
                  label={t('aml.has_pep_associate')}
                  options={yesNo}
                  value={values.hasPepAssociate}
                  onChange={(v) => patch('hasPepAssociate', v)}
                  error={errors.hasPepAssociate}
                  testIDPrefix="aml.pep-associate"
                  columns={2}
                />
                {/* Declaring PEP is an underwriting outcome, not a form error:
                    the insurer's web service refuses it (error 0284) and a
                    person takes over. Said calmly, never in red, never a wall. */}
                {needsManualPepReview ? (
                  <ManualReviewNotice
                    title={t('aml.pep_review_title')}
                    body={t('aml.pep_review_body', { partner: QUALITAS_AML_PARTNER.displayName })}
                    testID="aml.pep-notice"
                  />
                ) : null}
                <ChoicePillGroup
                  label={t('aml.tax_haven')}
                  helper={t('aml.tax_haven_helper')}
                  options={yesNo}
                  value={values.hasTaxHavenRelations}
                  onChange={(v) => patch('hasTaxHavenRelations', v)}
                  error={errors.hasTaxHavenRelations}
                  testIDPrefix="aml.tax-haven"
                  columns={2}
                />
                <ChoicePillGroup
                  label={t('aml.illicit_judicial')}
                  helper={t('aml.illicit_judicial_helper')}
                  options={yesNo}
                  value={values.hasIllicitOrJudicialHistory}
                  onChange={(v) => patch('hasIllicitOrJudicialHistory', v)}
                  error={errors.hasIllicitOrJudicialHistory}
                  testIDPrefix="aml.illicit"
                  columns={2}
                />
              </Group>
            </AnimatedBlock>

            <AnimatedBlock entering={enter(4)}>
              <SectionLabel
                title={t('aml.section_disclaimer', { partner: QUALITAS_AML_PARTNER.displayName })}
                colors={colors}
              />
              {/*
                Not a `Group`. That wrapper clips overflow, which ate the
                inner document scroller and left the accept checkbox dead.
                The document is the only thing on this card, so it owns the
                surface itself.
              */}
              <View
                style={{
                  backgroundColor: colors.surface,
                  borderRadius: RADIUS.lg,
                  borderWidth: 1,
                  borderColor: colors.border,
                  padding: SPACING.md,
                }}
              >
                <QualitasDisclaimerScroll
                  recessed
                  accepted={values.disclaimerAccepted}
                  scrollCompleted={values.disclaimerScrollCompleted}
                  onAcceptedChange={(v) => patch('disclaimerAccepted', v)}
                  onScrollCompleted={markDisclaimerPresented}
                  error={errors.disclaimerAccepted || errors.disclaimerScrollCompleted}
                />
              </View>
            </AnimatedBlock>

            {/* Art. 44 D.L. 144/2024 — the transfer abroad needs its own prior
                and express consent. It gets its own section, its own box and
                its own sentence: the moment a person authorizes their data to
                leave the country cannot ride along with anything else, and it
                arrives unticked because a default is not a decision. */}
            <AnimatedBlock entering={enter(5)}>
              <SectionLabel title={t('aml.section_cross_border')} colors={colors} />
              <Group colors={colors}>
                {/* Same shape as the intro card above: icon + title on one
                    row, prose at full width underneath. */}
                <View style={{ gap: SPACING.sm }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
                    <View
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: RADIUS.full,
                        backgroundColor: colors.surfaceSubtle,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Globe size={22} color={colors.info} strokeWidth={1.8} />
                    </View>
                    <Text
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 16,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {t('aml.cross_border_title')}
                    </Text>
                  </View>
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      lineHeight: 20,
                    }}
                  >
                    {t('aml.cross_border_body', { partner: QUALITAS_AML_PARTNER.displayName })}
                  </Text>
                </View>

                {/* No `error` prop on purpose: declining is a lawful answer,
                    so this control never turns red. What is missing is said
                    once, calmly, in the notice below it. */}
                <Checkbox
                  checked={values.crossBorderTransferAccepted}
                  onChange={(v) => patch('crossBorderTransferAccepted', v)}
                  required
                  accessibilityLabel={t('aml.cross_border_label', {
                    partner: QUALITAS_AML_PARTNER.displayName,
                  })}
                  testID="aml.cross-border-consent"
                  label={
                    <Text
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 14,
                        lineHeight: 20,
                        color: colors.textPrimary,
                        flex: 1,
                      }}
                    >
                      {t('aml.cross_border_label', { partner: QUALITAS_AML_PARTNER.displayName })}
                    </Text>
                  }
                />

                {!values.crossBorderTransferAccepted ? (
                  <ManualReviewNotice
                    title={t('aml.cross_border_declined_title')}
                    body={t('aml.cross_border_declined_body', {
                      partner: QUALITAS_AML_PARTNER.displayName,
                    })}
                    testID="aml.cross-border-declined"
                  />
                ) : null}
              </Group>
            </AnimatedBlock>
          </View>
        </KeyboardAwareView>

        {/* Sticky Apple-style bottom CTA */}
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: colors.border,
            backgroundColor: colors.background,
            paddingHorizontal: SPACING.lg,
            paddingTop: SPACING.md,
            paddingBottom: insets.bottom + SPACING.md,
          }}
          testID="aml.action-bar"
        >
          <Button
            title={needsManualPepReview ? t('aml.submit_manual_review') : t('aml.submit')}
            onPress={() => void handleSubmit()}
            loading={submitMutation.isPending}
            disabled={submitMutation.isPending}
            testID="aml.submit-button"
          />
        </View>
      </View>
    )
  }

  // The container renders from ONE JSX position: each of the three sibling
  // returns used to re-wrap its own copy, tearing the screen down on every
  // transition. ScreenState owns the region that varies, so a failed
  // bootstrap now offers a retry instead of a spinner that never stops.
  return (
    <ScreenContainer scroll={false} variant="content">
      <ScreenState
        query={{
          data: bootstrap.data,
          // `hydrated` gates on the local copy seeded from the query, but the
          // error has to win: gating on `!hydrated` alone kept a failed
          // bootstrap spinning forever, because hydration never happens.
          isLoading: bootstrap.isLoading || (!hydrated && !bootstrap.isError),
          isError: bootstrap.isError,
          refetch: bootstrap.refetch,
        }}
        skeleton={
          <>
            <ScreenHeader title={t('aml.title')} backTestID="aml.back" />
            {/* Shaped like the form it replaces — section label, then a group
                of inset fields — so the swap to content does not jump. A bare
                spinner here was both a STANDARDS §2 violation and the widest
                layout shift on the screen. */}
            <View style={{ padding: SPACING.lg, gap: SPACING.md }}>
              <Skeleton width="45%" height={16} borderRadius={RADIUS.xs} />
              <View
                style={{
                  backgroundColor: colors.surface,
                  borderRadius: RADIUS.lg,
                  borderWidth: 1,
                  borderColor: colors.border,
                  padding: SPACING.md,
                  gap: SPACING.md,
                }}
              >
                <Skeleton width="100%" height={AML_FIELD_HEIGHT} borderRadius={RADIUS.md} />
                <Skeleton width="100%" height={AML_FIELD_HEIGHT} borderRadius={RADIUS.md} />
                <Skeleton width="100%" height={AML_FIELD_HEIGHT} borderRadius={RADIUS.md} />
              </View>
              <Skeleton width="45%" height={16} borderRadius={RADIUS.xs} />
              <View
                style={{
                  backgroundColor: colors.surface,
                  borderRadius: RADIUS.lg,
                  borderWidth: 1,
                  borderColor: colors.border,
                  padding: SPACING.md,
                  gap: SPACING.md,
                }}
              >
                <Skeleton width="100%" height={AML_FIELD_HEIGHT} borderRadius={RADIUS.md} />
                <Skeleton width="100%" height={AML_FIELD_HEIGHT} borderRadius={RADIUS.md} />
              </View>
            </View>
          </>
        }
      >
        {renderBody}
      </ScreenState>
    </ScreenContainer>
  )
}
